import { lang } from "../core/variables-global.js";
import { marqueLabels } from "../core/l10n.js";

function initMarquesSelector() {
  // Variables globales
  const MINIMUM_INPUT_LENGTH = 2;
  let selectedMarques = window.selectedMarques || [];
  let availableMarques = window.availableMarques || [];
  let originalMarques = window.originalMarques || [];
  let isLoading = false;
  let nextId = 0;
  const instances = [];

  function init() {
    const marquesSelectors = document.querySelectorAll('.js-selection-marques');
    const moduleRecherceAvancee = document.querySelector('.js-recherche-avancee');

    if (marquesSelectors.length === 0) return null;

    marquesSelectors.forEach((moduleMarquesSelector) => {
      const isModal = moduleMarquesSelector.closest('.js-hq-modale') !== null;
      const input = moduleMarquesSelector.querySelector('.js-input-marque');
      const options = moduleMarquesSelector.querySelector('.js-options');
      const selectedItemsContainer = moduleMarquesSelector.querySelector('.js-selected-items');
      const announce = moduleMarquesSelector.querySelector('.js-announce');

      if (!input || !options || !selectedItemsContainer || !announce) return;

      let cursor = -1;
      const inputAvance = moduleRecherceAvancee.querySelectorAll('.js-input-avance');
    //  const lang = document.documentElement.getAttribute("lang") || "fr";
      const texts = getLocalizedTexts(lang);

      // Configure les événements pour cette instance
      function setupEventListeners() {
        input.addEventListener('input', e => renderOptions(e.target.value));
        input.addEventListener('focus', handleInputFocus);
        input.addEventListener('keydown', handleKeyboardNavigation);

        document.addEventListener("click", e => {
          if (!options.contains(e.target) && e.target !== input) {
            closeOptionsList();
          }
        });

        document.addEventListener("keydown", e => {
          if (e.key === "Escape" && options.classList.contains("active")) {
            closeOptionsList();
            input.blur();
          }
        });

        inputAvance.forEach(element => {
          element.addEventListener("keydown", e => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          });
        });

        options.addEventListener('click', handleOptionClick);
        selectedItemsContainer.addEventListener('click', handleRemoveItemClick);

        document.addEventListener("keydown", e => {
          if (e.key === "Enter") {
            const optionElement = options.querySelector(".option.selected");
            if (optionElement) {
              e.preventDefault();
              const { id, name } = optionElement.dataset;
              if (id && name) {
                closeOptionsList();
                selectMarque(id, name);
              }
            }
          }
        });
      }

      // Gère le focus sur l'input
      function handleInputFocus() {
        updateDisplay();
      if (input.value.trim().length > 0 ) {
          renderOptions(input.value);
        }
      }

      // Gère la navigation au clavier
      function handleKeyboardNavigation(e) {
        if (!options.classList.contains("active")) return;

        const listItems = options.querySelectorAll(".option");
        const total = listItems.length;

        switch (e.key) {
          case "Tab":
            closeOptionsList();
            break;
          case "Escape":
            closeOptionsList();
            break;
          case "ArrowDown":
            e.preventDefault();
            updateCursor(cursor < total - 1 ? cursor + 1 : 0);
            break;
          case "ArrowUp":
            e.preventDefault();
            updateCursor(cursor > 0 ? cursor - 1 : total - 1);
            break;
        }
      }

      // Gère le clic sur une option
      function handleOptionClick(e) {
        const optionElement = e.target.closest(".option");
        if (!optionElement) return;

        e.preventDefault();
        e.stopPropagation();

        const { id, name } = optionElement.dataset;
        if (id && name) {
          closeOptionsList();
          selectMarque(id, name);
        }
      }

      // Gère le clic sur un bouton de suppression
      function handleRemoveItemClick(e) {
        e.preventDefault();
        if (e.target.classList.contains("js-remove") || e.target.closest(".js-remove")) {
          const selectedItem = e.target.closest(".selected-item");
          if (selectedItem) {
            removeMarque(selectedItem.dataset.id);
          }
        }
      }

      // Affiche les options de recherche
      const renderOptions = debounce((query) => {
        const trimmedQuery = query?.trim();

        if (!trimmedQuery) {
          closeOptionsList();
          return;
        }

        if (isLoading) {
          displayOptionsWithState(`<li class="info-text">${texts.loading}</li>`);
          return;
        }

        // Regarde pour un minimum de caractères avant d'afficher les options
        if (trimmedQuery.length >= MINIMUM_INPUT_LENGTH) {
          const queryLower = trimmedQuery.toLowerCase();
          const filteredOptions = availableMarques.filter(({ name }) =>
            name.toLowerCase().includes(queryLower)
          );

          resetCursor();

          if (filteredOptions.length === 0) {
            displayOptionsWithState(`<li class="info-text">${texts.noResults}</li>`);
            announce.textContent = texts.noResults;
          } else {
            const optionsHTML = filteredOptions
              .map(({ id, name }) => `
                <li class="option"
                    id="option-${id}"
                    data-id="${id}"
                    data-name="${name}"
                    role="option"
                    aria-selected="false">
                    ${escapeHTML(name)}
                </li>
              `)
              .join("");

            displayOptionsWithState(optionsHTML);
            announce.textContent = `${filteredOptions.length} ${texts.suggestions}`;

            // Prevent default mousedown to avoid focus loss
            options.querySelectorAll(".option").forEach(option => {
              option.addEventListener("mousedown", e => e.preventDefault(), { once: true });
            });
          }
        } else {
          options.innerHTML = "";
        }

        if (isModal) positionOptionsInModal();

      }, 300);

      // Helper function to display options with consistent state management
      const displayOptionsWithState = (innerHTML) => {
        options.innerHTML = innerHTML;
        options.classList.add("active");
        input.setAttribute("aria-expanded", "true");
      };

      // Positionne les options dans une modale
      const positionOptionsInModal = () => {
        if (!isModal) return;

        // Use CSS custom property for better maintainability
        options.style.setProperty("z-index", "1000001");
      };

      // Ferme la liste d'options
      function closeOptionsList() {
        options.classList.remove("active");
        input.setAttribute("aria-expanded", "false");
        resetCursor();
        announce.textContent = "";
        options.style.position = "";
        options.style.top = "";
        options.style.left = "";
        options.style.width = "";
        options.style.zIndex = "";
      }

      // Met à jour le curseur
      function updateCursor(newPosition) {
        const listItems = options.querySelectorAll(".option");

        listItems.forEach((item, idx) => {
          item.classList.toggle("selected", idx === newPosition);
          item.setAttribute("aria-selected", idx === newPosition);
        });

        cursor = newPosition;

        if (listItems[cursor]) {
          input.setAttribute("aria-activedescendant", listItems[cursor].id);
          listItems[cursor].scrollIntoView({ block: "nearest" });
        }
      }

      // Réinitialise le curseur
      function resetCursor() {
        cursor = -1;
        input.removeAttribute("aria-activedescendant");
      }

      // Met à jour l'affichage des marques sélectionnées
      function updateDisplay() {
        selectedItemsContainer.innerHTML = selectedMarques.map(marque => `
          <button class="selected-item js-remove" data-id="${escapeHTML(marque.id)}" type="button" tabindex="0">
            ${escapeHTML(marque.name)}
            <span class="remove-btn" aria-label="${texts.removeLabel} ${escapeHTML(marque.name)}"></span>
          </button>
        `).join('');

        selectedItemsContainer.querySelectorAll(".js-remove").forEach(button => {
          button.addEventListener("keydown", function (e) {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              const id = this.dataset.id;
              removeMarque(id);
            }
          });

          button.addEventListener("click", function (e) {
            e.preventDefault();
            e.stopPropagation();
            const id = this.dataset.id;
            removeMarque(id);
          });
        });
      }

      // Sélectionne une marque
      function selectMarque(id, name) {
        id = String(id);
        if (!id || !name) return;

        const normalizedName = name.toLowerCase().trim();

        if (selectedMarques.some(marque =>
          marque.name.toLowerCase().trim() === normalizedName || marque.id === id
        )) {
          announce.textContent = `${name} ${texts.alreadySelected}`;
          input.value = "";
          input.focus();
          return;
        }

        selectedMarques.push({ id, name });
        window.selectedMarques = [...selectedMarques];

        availableMarques = availableMarques.filter(marque =>
          marque.id !== id && marque.name.toLowerCase().trim() !== normalizedName
        );
        window.availableMarques = availableMarques;

        updateAllInstances();
        input.value = "";
        announce.textContent = `${name} ${texts.added}`;
        input.focus();

        if (typeof accordeonModule !== "undefined" && accordeonModule.throttle && accordeonModule.handleAccordeonResize) {
          accordeonModule.throttle(accordeonModule.handleAccordeonResize, 0)();
        }
      }

      // Supprime une marque sélectionnée
      function removeMarque(id) {
        if (!id) return;

        const marqueToRestore = selectedMarques.find(marque => marque.id === id);
        if (!marqueToRestore) return;

        selectedMarques = selectedMarques.filter(marque => marque.id !== id);
        window.selectedMarques = [...selectedMarques];

        if (!availableMarques.some(marque =>
          marque.id === marqueToRestore.id ||
          marque.name.toLowerCase().trim() === marqueToRestore.name.toLowerCase().trim()
        )) {
          availableMarques.push(marqueToRestore);
          availableMarques.sort((a, b) => a.name.localeCompare(b.name));
          window.availableMarques = availableMarques;
        }

        updateAllInstances();
        announce.textContent = `${marqueToRestore.name} ${texts.removed}`;
        input.focus();

        if (typeof accordeonModule !== "undefined" && accordeonModule.throttle && accordeonModule.handleAccordeonResize) {
          accordeonModule.throttle(accordeonModule.handleAccordeonResize, 0)();
        }
      }

      setupEventListeners();

      const instance = {
        updateDisplay,
        positionOptionsInModal
      };

      instances.push(instance);
    });

  }


  // Échappe les caractères HTML et valide les IDs
  function escapeHTML(str) {
    if (typeof str !== "string") return "";

    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#x27;")
      .replace(/\//g, "&#x2F;");
  }

  // Valide et nettoie les IDs
  function sanitizeId(id) {
    if (typeof id !== "string" && typeof id !== "number") return "";
    return String(id).replace(/[^a-zA-Z0-9-_]/g, "");
  }

  // Limite la fréquence d'exécution d'une fonction
  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Obtient les textes selon la langue
  function getLocalizedTexts(lang) {
    const isFrench = lang.toLowerCase().startsWith("fr");

    if(marqueLabels) {
      return {
        suggestions: marqueLabels.suggestions.label[lang],
        noResults: marqueLabels.noResult.label[lang],
        loading: marqueLabels.loading.label[lang],
        added: marqueLabels.added.label[lang],
        removed: marqueLabels.removed.label[lang],
        removeLabel: marqueLabels.removeLabel.label[lang],
        alreadySelected: marqueLabels.alreadySelected.label[lang]
      };
    }

  }

  // Met à jour tous les sélecteurs
  function updateAllInstances() {
    instances.forEach(instance => {
      if (instance.updateDisplay) instance.updateDisplay();
    });
  }

  // Retourne les marques sélectionnées
  function getSelectedMarques() {
    return selectedMarques.map(marque => ({
      id: marque.id,
      name: marque.name
    }));
  }

  // Charge les données de marques
  function loadMarques(data) {
    try {
      isLoading = true;
      const marqueData = data.data || data;
      const uniqueMarques = new Map();

      marqueData.forEach(item => {
        if (!item.marque) return;
        const normalizedName = item.marque.trim().toLowerCase();
        if (!uniqueMarques.has(normalizedName) ||
          (item.marque !== item.marque.toLowerCase() &&
            uniqueMarques.get(normalizedName).name === uniqueMarques.get(normalizedName).name.toLowerCase())) {
          uniqueMarques.set(normalizedName, {
            id: nextId++,
            name: item.marque.trim()
          });
        }
      });

      availableMarques = Array.from(uniqueMarques.values()).sort((a, b) => a.name.localeCompare(b.name));
      window.availableMarques = availableMarques;
      originalMarques = JSON.parse(JSON.stringify(availableMarques));
      window.originalMarques = originalMarques;
      updateAllInstances();
    } catch (error) {
      console.error("Erreur lors du chargement des marques:", error);
      const marquesSelectors = document.querySelectorAll(".js-selection-marques");
      marquesSelectors.forEach(selector => {
        const optionsElement = selector.querySelector('.js-options');
        if (optionsElement) {
          //const lang = selector.closest("[lang]")?.getAttribute("lang") || document.documentElement.getAttribute("lang") || "fr";
          const errorText = lang.startsWith("fr") ? "Erreur de chargement" : "Loading error";
          optionsElement.innerHTML = "";

          // Créer l'élément li
          const errorItem = document.createElement("li");
          errorItem.textContent = errorText;
          errorItem.style.padding = "10px";
          errorItem.style.color = "red";

          // Ajouter au conteneur
          optionsElement.appendChild(errorItem);
        }
      });
    } finally {
      isLoading = false;
    }
  }

  // Réinitialise les marques sélectionnées
  function resetSelectedMarques() {
    selectedMarques = [];
    window.selectedMarques = [];

    if (originalMarques && originalMarques.length > 0) {
      window.availableMarques = [...originalMarques];
      availableMarques = [...originalMarques];
    }

    updateAllInstances();
    isLoading = false;

    if (typeof accordeonModule !== "undefined" && accordeonModule.throttle && accordeonModule.handleAccordeonResize) {
      accordeonModule.throttle(accordeonModule.handleAccordeonResize, 0)();
    }
  }

  return {
    init,
    getSelectedMarques,
    loadMarques,
    resetSelectedMarques
  };
}



window.initMarquesSelector = initMarquesSelector();
export default window.initMarquesSelector;
